/**
 * Shared line-item editor components extracted from estimates/detail.tsx so
 * they can be re-used inside the Change Orders tab of projects/detail.tsx.
 *
 * Exports:
 *   - EstimateLineItemSection  — the main card wrapper (takes estimateId)
 *   - GROUP_ACCENT_COLORS       — used by the parent when auto-picking colors
 *
 * All sub-components (GroupHeader, LineItemRow, PerItemWorkshop, AddItemSearch,
 * CreateLineItemModal, CreateNewCostModal, CostRow, …) are kept private to
 * this module.
 */

import { useState, useEffect, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useGetEstimate,
  useCreateLineItem,
  useUpdateLineItem,
  useDeleteLineItem,
  useUpdateLineItemNote,
  useReorderEstimateLineItems,
  useListCosts,
  useCreateCost,
  useUpdateCost,
  useDeleteCost,
  useListInternalCatalog,
  useListProducts,
  useCreateProduct,
  useCreateInternalCatalogItem,
  useCreateLineItemGroup,
  useUpdateLineItemGroup,
  useDeleteLineItemGroup,
  getGetEstimateQueryKey,
  getListProductsQueryKey,
  getListCostsQueryKey,
  getListInternalCatalogQueryKey,
  type LineItemGroup,
} from "@workspace/api-client-react";
import {
  InternalWorkshopView,
  breakdownFromMaterials,
  PARENT_CATEGORIES as SHARED_PARENT_CATEGORIES,
  costTypeToParent as sharedCostTypeToParent,
  type ParentCategory as SharedParentCategory,
} from "@/components/internal-workshop-view";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { useAutosaveField } from "@/hooks/use-autosave-field";
import { emitTourEvent } from "@/hooks/use-guided-tour";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Plus, Search, Loader2, Trash2, Copy, Group, ChevronDown, ChevronUp,
  Edit, Lock, Save, EyeOff, Check,
} from "lucide-react";
import { useTargetMargin } from "@/hooks/use-target-margin";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const GROUP_ACCENT_COLORS = [
  { label: "Blue",   hex: "#3b82f6" },
  { label: "Green",  hex: "#22c55e" },
  { label: "Red",    hex: "#ef4444" },
  { label: "Yellow", hex: "#eab308" },
  { label: "Purple", hex: "#a855f7" },
  { label: "Orange", hex: "#f97316" },
];

const PARENT_CATEGORIES = SHARED_PARENT_CATEGORIES;
type ParentCategory = SharedParentCategory;
const costTypeToParent = sharedCostTypeToParent;


// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const quickProductSchema = z.object({
  name: z.string().min(1, "Required"),
  description: z.string().optional(),
  unitPrice: z.coerce.number().min(0),
  unit: z.string().optional(),
});

const quickCatalogSchema = z.object({
  name: z.string().min(1, "Required"),
  description: z.string().optional(),
  unitCost: z.coerce.number().min(0),
  unit: z.string().optional(),
});

const createLineItemModalSchema = z.object({
  name: z.string().min(1, "Required"),
  description: z.string().optional(),
  quantity: z.coerce.number().min(0.01, "Must be > 0").max(99999999, "Maximum value is 99,999,999"),
  // Negative prices are allowed so a discount or credit can be added as a line item.
  unitPrice: z.coerce.number().min(-99999999.99, "Minimum value is -99,999,999").max(99999999.99, "Maximum value is 99,999,999"),
  saveAsProduct: z.boolean().optional(),
});

const createCostModalSchema = z.object({
  name: z.string().min(1, "Required"),
  type: z.enum(["labour", "materials", "fees"]).default("materials"),
  quantity: z.coerce.number().min(0.01, "Must be > 0"),
  unitCost: z.coerce.number().min(0, "Must be ≥ 0"),
  unit: z.string().optional(),
  supplier: z.string().optional(),
  notes: z.string().optional(),
  saveToCatalog: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// QuickCreateProductDialog
// ---------------------------------------------------------------------------

function QuickCreateProductDialog({ initialName, onCreated, onClose }: {
  initialName: string;
  onCreated: (product: { id: number; name: string; description?: string | null; unitPrice: number; unit?: string | null }) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const createProduct = useCreateProduct();
  const { toast } = useToast();

  const form = useForm<z.infer<typeof quickProductSchema>>({
    resolver: zodResolver(quickProductSchema),
    defaultValues: { name: initialName, description: "", unitPrice: 0, unit: "" },
  });

  useEffect(() => {
    form.reset({ name: initialName, description: "", unitPrice: 0, unit: "" });
  }, [initialName]);

  const onSubmit = (data: z.infer<typeof quickProductSchema>) => {
    createProduct.mutate({
      data: {
        name: data.name,
        description: data.description || null,
        unitPrice: data.unitPrice,
        unit: data.unit || null,
      }
    }, {
      onSuccess: (newProduct) => {
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
        toast({ title: `Product "${newProduct.name}" created` });
        onCreated(newProduct);
      },
      onError: () => {
        toast({ title: "Failed to create product", variant: "destructive" });
      }
    });
  };

  return (
    <DialogContent>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Create Product</DialogTitle>
            <DialogDescription>Add a new product to the catalog and use it in this line item.</DialogDescription>
          </DialogHeader>
          <FormField control={form.control} name="name" render={({ field }) => (
            <FormItem><FormLabel>Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
          )} />
          <FormField control={form.control} name="description" render={({ field }) => (
            <FormItem>
              <FormLabel>Description <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
              <FormControl><Textarea className="min-h-[60px]" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )} />
          <div className="grid grid-cols-2 gap-4">
            <FormField control={form.control} name="unitPrice" render={({ field }) => (
              <FormItem><FormLabel>Unit Price ($)</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="unit" render={({ field }) => (
              <FormItem>
                <FormLabel>Unit <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                <FormControl><Input placeholder="each, hr, sqft…" {...field} /></FormControl>
              </FormItem>
            )} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={createProduct.isPending}>
              {createProduct.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Create & Select
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
}

// ---------------------------------------------------------------------------
// QuickCreateCatalogDialog
// ---------------------------------------------------------------------------

function QuickCreateCatalogDialog({ initialName, onCreated, onClose }: {
  initialName: string;
  onCreated: (item: { id: number; name: string; unitCost: number; unit?: string | null }) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const createCatalogItem = useCreateInternalCatalogItem();
  const { toast } = useToast();

  const form = useForm<z.infer<typeof quickCatalogSchema>>({
    resolver: zodResolver(quickCatalogSchema),
    defaultValues: { name: initialName, description: "", unitCost: 0, unit: "" },
  });

  useEffect(() => {
    form.reset({ name: initialName, description: "", unitCost: 0, unit: "" });
  }, [initialName]);

  const onSubmit = (data: z.infer<typeof quickCatalogSchema>) => {
    createCatalogItem.mutate({
      data: {
        name: data.name,
        description: data.description || null,
        unitCost: data.unitCost,
        unit: data.unit || null,
      }
    }, {
      onSuccess: (newItem) => {
        queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
        toast({ title: `Cost "${newItem.name}" added to catalog` });
        onCreated(newItem);
      },
      onError: () => {
        toast({ title: "Failed to create catalog item", variant: "destructive" });
      }
    });
  };

  return (
    <DialogContent>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Create Cost Catalog Item</DialogTitle>
            <DialogDescription>Add a new item to the internal cost catalog and use it here.</DialogDescription>
          </DialogHeader>
          <FormField control={form.control} name="name" render={({ field }) => (
            <FormItem><FormLabel>Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
          )} />
          <FormField control={form.control} name="description" render={({ field }) => (
            <FormItem>
              <FormLabel>Description <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
              <FormControl><Textarea className="min-h-[60px]" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )} />
          <div className="grid grid-cols-2 gap-4">
            <FormField control={form.control} name="unitCost" render={({ field }) => (
              <FormItem><FormLabel>Unit Cost ($)</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="unit" render={({ field }) => (
              <FormItem>
                <FormLabel>Unit <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                <FormControl><Input placeholder="hr, sqft, ea…" {...field} /></FormControl>
              </FormItem>
            )} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={createCatalogItem.isPending}>
              {createCatalogItem.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Create & Select
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
}

// ---------------------------------------------------------------------------
// CreateLineItemModal
// ---------------------------------------------------------------------------

function CreateLineItemModal({ estimateId, initialName, groupId, onClose, onAdded }: {
  estimateId: number;
  initialName: string;
  groupId?: number;
  /** Cancel: back to the search dialog. */
  onClose: () => void;
  /** Item added: close the whole add-item flow. */
  onAdded: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const createLineItem = useCreateLineItem();
  const createProduct = useCreateProduct();

  const form = useForm<z.infer<typeof createLineItemModalSchema>>({
    resolver: zodResolver(createLineItemModalSchema),
    defaultValues: { name: initialName, description: "", quantity: 1, unitPrice: 0, saveAsProduct: false },
  });

  useEffect(() => {
    form.reset({ name: initialName, description: "", quantity: 1, unitPrice: 0, saveAsProduct: false });
  }, [initialName]);

  const onSubmit = async (data: z.infer<typeof createLineItemModalSchema>) => {
    let productId: number | null = null;
    if (!productId && data.saveAsProduct) {
      try {
        const newProduct = await new Promise<{ id: number }>((resolve, reject) => {
          createProduct.mutate(
            { data: { name: data.name, description: data.description || null, unitPrice: data.unitPrice, unit: null } },
            { onSuccess: resolve, onError: reject }
          );
        });
        productId = newProduct.id;
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      } catch {
        toast({ title: "Failed to save product", variant: "destructive" });
        return;
      }
    }

    const customerDescription = data.description?.trim() ? data.description : data.name;
    createLineItem.mutate(
      { estimateId, data: { productId, description: customerDescription, quantity: data.quantity, unitPrice: data.unitPrice, groupId: groupId ?? null } },
      {
        onSuccess: (newItem) => {
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          if (newItem?.id) queryClient.invalidateQueries({ queryKey: getListCostsQueryKey(estimateId, newItem.id) });
          toast({ title: "Line item added" });
          emitTourEvent("quote.line-item-added");
          onAdded();
        },
        onError: () => toast({ title: "Failed to add item", variant: "destructive" })
      }
    );
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DialogHeader>
              <DialogTitle>New Line Item</DialogTitle>
              <DialogDescription>Add a new service or item. Visible to the customer.</DialogDescription>
            </DialogHeader>

            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem>
                <FormLabel>Service / Item Name <span className="text-muted-foreground font-normal text-xs">(shown on quote)</span></FormLabel>
                <FormControl><Input {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Description <span className="text-muted-foreground font-normal">(optional detail shown to customer)</span></FormLabel>
                <FormControl><Textarea className="min-h-[60px]" placeholder="Leave blank to use the name above..." {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="quantity" render={({ field }) => (
                <FormItem><FormLabel>Quantity</FormLabel><FormControl><Input type="number" step="0.01" min="0.01" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="unitPrice" render={({ field }) => (
                <FormItem><FormLabel>Unit Price ($)</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
            <FormField control={form.control} name="saveAsProduct" render={({ field }) => (
              <FormItem>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={field.value ?? false}
                    onChange={e => field.onChange(e.target.checked)}
                    className="rounded border-input"
                  />
                  <span className="text-sm text-muted-foreground">Save as new product in catalog for reuse</span>
                </label>
              </FormItem>
            )} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={createLineItem.isPending || createProduct.isPending}>
                {(createLineItem.isPending || createProduct.isPending) && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Add Line Item
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// AddItemSearch
// ---------------------------------------------------------------------------

export function AddItemSearch({ estimateId, groupId, onClose }: { estimateId: number; groupId?: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const [search, setSearch] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalName, setCreateModalName] = useState("");
  const createLineItem = useCreateLineItem();

  const { data: products } = useListProducts(
    search ? { search } : {},
    { query: { queryKey: getListProductsQueryKey(search ? { search } : {}), enabled: true } }
  );

  const addFromProduct = (product: { id: number; name: string; description?: string | null; unitPrice: number }) => {
    createLineItem.mutate(
      { estimateId, data: { productId: product.id, description: product.description || product.name, quantity: 1, unitPrice: product.unitPrice, groupId: groupId ?? null } },
      {
        onSuccess: (newItem) => {
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          if (newItem?.id) queryClient.invalidateQueries({ queryKey: getListCostsQueryKey(estimateId, newItem.id) });
          emitTourEvent("quote.line-item-added");
          onClose();
        },
        onError: () => toast({ title: "Failed to add item", variant: "destructive" })
      }
    );
  };

  const openCreateModal = (name: string) => {
    setCreateModalName(name);
    setCreateModalOpen(true);
  };

  if (createModalOpen) {
    return (
      <CreateLineItemModal
        estimateId={estimateId}
        initialName={createModalName}
        groupId={groupId}
        onClose={() => setCreateModalOpen(false)}
        onAdded={onClose}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add Line Item</DialogTitle>
          <DialogDescription>Search your products or create a custom item.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Search products..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
              disabled={createLineItem.isPending}
            />
            {createLineItem.isPending && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          </div>
          <div className="border rounded-md overflow-hidden max-h-56 overflow-y-auto">
            {products?.map(p => (
              <button
                key={p.id}
                type="button"
                className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-muted/60 transition-colors border-b last:border-0"
                onClick={() => addFromProduct(p)}
              >
                <div>
                  <p className="text-sm font-medium">{p.name}</p>
                  {p.description && <p className="text-xs text-muted-foreground truncate max-w-[220px]">{p.description}</p>}
                </div>
                <span className="text-sm font-semibold text-primary shrink-0 ml-2">{formatCurrency(Number(p.unitPrice))}</span>
              </button>
            ))}
            {search.trim() && !products?.some(p => p.name.toLowerCase() === search.trim().toLowerCase()) && (
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/60 transition-colors text-sm text-primary font-medium border-t"
                onClick={() => openCreateModal(search.trim())}
              >
                <Plus className="w-3.5 h-3.5 shrink-0" />
                Create new service: "{search.trim()}"
              </button>
            )}
            {!search && (!products || products.length === 0) && (
              <p className="text-xs text-muted-foreground px-3 py-3">Type to search products, or a name to create a custom item.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// GroupHeader
// ---------------------------------------------------------------------------

export function GroupHeader({
  group,
  estimateId,
  items,
  allGroups,
  children,
}: {
  group: LineItemGroup;
  estimateId: number;
  items: any[];
  allGroups: LineItemGroup[];
  children: ReactNode;
}) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(Boolean(group.collapsedByDefault ?? false));
  const [isEditing, setIsEditing] = useState(!group.name || group.name === "New Group");
  const [name, setName] = useState(group.name ?? "");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const updateGroup = useUpdateLineItemGroup();
  const deleteGroup = useDeleteLineItemGroup();
  const createLineItem = useCreateLineItem();
  const createGroupDup = useCreateLineItemGroup();
  const createCostDup = useCreateCost();
  const updateNoteDup = useUpdateLineItemNote();
  const [isDuplicatingGroup, setIsDuplicatingGroup] = useState(false);

  const sortedAllGroups = [...allGroups].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const myIndex = sortedAllGroups.findIndex(g => g.id === group.id);
  const canMoveUp = myIndex > 0;
  const canMoveDown = myIndex < sortedAllGroups.length - 1;

  const handleMove = (direction: "up" | "down") => {
    const swapIdx = direction === "up" ? myIndex - 1 : myIndex + 1;
    const swapGroup = sortedAllGroups[swapIdx];
    if (!swapGroup) return;
    const mySort = group.sortOrder ?? myIndex;
    const swapSort = swapGroup.sortOrder ?? swapIdx;
    Promise.all([
      updateGroup.mutateAsync({ estimateId, id: group.id, data: { sortOrder: swapSort } }),
      updateGroup.mutateAsync({ estimateId, id: swapGroup.id, data: { sortOrder: mySort } }),
    ]).then(() => queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) }))
      .catch(() => toast({ title: "Failed to reorder groups", variant: "destructive" }));
  };

  const handleDuplicateGroup = async () => {
    setIsDuplicatingGroup(true);
    try {
      const mySortOrder = group.sortOrder ?? myIndex;
      const nextGroup = sortedAllGroups[myIndex + 1];
      const newSortOrder = nextGroup
        ? (mySortOrder + (nextGroup.sortOrder ?? myIndex + 1)) / 2
        : mySortOrder + 1;
      const newGroup = await new Promise<{ id: number }>((resolve, reject) => {
        createGroupDup.mutate(
          { estimateId, data: { name: `${group.name || "Untitled Group"} (copy)`, sortOrder: newSortOrder } },
          { onSuccess: resolve, onError: reject }
        );
      });
      for (const originalItem of items) {
        const newItem = await new Promise<{ id: number }>((resolve, reject) => {
          createLineItem.mutate(
            { estimateId, data: { description: originalItem.description, quantity: originalItem.quantity, unitPrice: originalItem.unitPrice, productId: originalItem.productId ?? null, groupId: newGroup.id } },
            { onSuccess: resolve, onError: reject }
          );
        });
        const materials = originalItem.materials ?? [];
        for (const mat of materials) {
          await new Promise<void>((resolve, reject) => {
            createCostDup.mutate(
              { estimateId, lineItemId: newItem.id, data: { name: mat.name, quantity: mat.quantity, unitCost: mat.unitCost, unit: mat.unit ?? null, supplier: mat.supplier ?? null, notes: mat.notes ?? null, type: (mat.type || "materials") as "labour" | "materials" | "fees", catalogItemId: mat.catalogItemId ?? null } },
              { onSuccess: () => resolve(), onError: reject }
            );
          });
        }
        if (originalItem.privateNote) {
          await new Promise<void>((resolve, reject) => {
            updateNoteDup.mutate(
              { estimateId, lineItemId: newItem.id, data: { privateNote: originalItem.privateNote! } },
              { onSuccess: () => resolve(), onError: reject }
            );
          });
        }
      }
      queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
      toast({ title: "Group duplicated" });
    } catch {
      toast({ title: "Failed to duplicate group", variant: "destructive" });
    } finally {
      setIsDuplicatingGroup(false);
    }
  };

  useEffect(() => {
    setName(group.name ?? "");
  }, [group.id, group.name]);

  useEffect(() => {
    if (!colorPickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
        setColorPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [colorPickerOpen]);

  const subtotal = items.reduce((s: number, li: any) => s + (li.total ?? 0), 0);

  const handleNameSave = () => {
    setIsEditing(false);
    const trimmed = name.trim();
    if (!trimmed) { setName(group.name ?? ""); return; }
    if (trimmed === group.name) return;
    updateGroup.mutate(
      { estimateId, id: group.id, data: { name: trimmed } },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) }),
        onError: () => { setName(group.name ?? ""); toast({ title: "Failed to save group name", variant: "destructive" }); },
      }
    );
  };

  const handleColorChange = (hex: string | null) => {
    setColorPickerOpen(false);
    updateGroup.mutate(
      { estimateId, id: group.id, data: { accentColor: hex } },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) }),
        onError: () => toast({ title: "Failed to update group color", variant: "destructive" }),
      }
    );
  };

  const handleDelete = () => {
    deleteGroup.mutate(
      { estimateId, id: group.id },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) }),
        onError: () => toast({ title: "Failed to delete group", variant: "destructive" }),
      }
    );
  };

  return (
    <div
      className="border-t-2 border-border"
      style={group.accentColor ? { borderLeft: `4px solid ${group.accentColor}` } : undefined}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/40 hover:bg-muted/60 transition-colors">
        <button
          type="button"
          onClick={() => setIsCollapsed(v => !v)}
          className="p-0.5 text-muted-foreground hover:text-foreground shrink-0"
          aria-label={isCollapsed ? "Expand group" : "Collapse group"}
        >
          {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>

        <div ref={colorPickerRef} className="relative shrink-0">
          <button
            type="button"
            onClick={() => setColorPickerOpen(v => !v)}
            className="w-4 h-4 rounded-full border-2 border-border hover:border-primary transition-colors"
            style={{ background: group.accentColor || "#e2e8f0" }}
            aria-label="Set group color"
            title="Set group color"
          />
          {colorPickerOpen && (
            <div className="absolute left-0 top-6 z-50 flex items-center gap-1 p-2 bg-popover border border-border rounded-md shadow-md">
              {GROUP_ACCENT_COLORS.map(color => (
                <button
                  key={color.hex}
                  type="button"
                  onClick={() => handleColorChange(color.hex)}
                  className="w-5 h-5 rounded-full border-2 hover:scale-110 transition-transform"
                  style={{
                    background: color.hex,
                    borderColor: group.accentColor === color.hex ? "#000" : "transparent",
                  }}
                  aria-label={`Set color ${color.label}`}
                  title={color.label}
                />
              ))}
              {group.accentColor && (
                <button
                  type="button"
                  onClick={() => handleColorChange(null)}
                  className="w-5 h-5 rounded-full border-2 border-border hover:scale-110 transition-transform bg-background flex items-center justify-center text-muted-foreground"
                  aria-label="Remove color"
                  title="Remove color"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>

        {isEditing ? (
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            onBlur={handleNameSave}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleNameSave(); } if (e.key === "Escape") { setName(group.name ?? ""); setIsEditing(false); } }}
            className="flex-1 text-sm font-semibold bg-transparent border-b border-primary outline-none py-0.5"
            placeholder="Group name"
          />
        ) : (
          <button
            type="button"
            className="flex-1 text-left text-sm font-semibold hover:text-primary transition-colors"
            onClick={() => setIsEditing(true)}
          >
            {group.name || "Untitled Group"}
          </button>
        )}

        <span className="text-xs text-muted-foreground shrink-0">{formatCurrency(subtotal)}</span>

        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => handleMove("up")}
            disabled={!canMoveUp}
            className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Move group up"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => handleMove("down")}
            disabled={!canMoveDown}
            className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Move group down"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                size="icon" variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={handleDuplicateGroup}
                disabled={isDuplicatingGroup}
                aria-label="Duplicate group"
              >
                {isDuplicatingGroup ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Duplicate group</TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                size="icon" variant="ghost"
                className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setDeleteOpen(true)}
                aria-label="Delete group"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete group</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete group?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the group header. Items inside will be moved to the ungrouped section.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!isCollapsed && children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CreateNewCostModal
// ---------------------------------------------------------------------------

function CreateNewCostModal({ estimateId, lineItemId, initialName, onClose }: {
  estimateId: number;
  lineItemId: number;
  initialName: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const createCost = useCreateCost();
  const createCatalogItem = useCreateInternalCatalogItem();
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);

  const form = useForm<z.infer<typeof createCostModalSchema>>({
    resolver: zodResolver(createCostModalSchema),
    defaultValues: { name: initialName, type: "materials", quantity: 1, unitCost: 0, unit: "", supplier: "", notes: "", saveToCatalog: false },
  });

  useEffect(() => {
    form.reset({ name: initialName, type: "materials", quantity: 1, unitCost: 0, unit: "", supplier: "", notes: "", saveToCatalog: false });
  }, [initialName]);

  const onSubmit = async (data: z.infer<typeof createCostModalSchema>) => {
    let catalogItemId: number | null = null;
    if (data.saveToCatalog) {
      try {
        const newCatalogItem = await new Promise<{ id: number }>((resolve, reject) => {
          createCatalogItem.mutate(
            { data: { name: data.name, description: data.notes || null, unitCost: data.unitCost, unit: data.unit || null, type: data.type } },
            { onSuccess: resolve, onError: reject }
          );
        });
        catalogItemId = newCatalogItem.id;
        queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
      } catch {
        toast({ title: "Failed to save to catalog", variant: "destructive" });
        return;
      }
    }

    createCost.mutate(
      { estimateId, lineItemId, data: { catalogItemId, name: data.name, quantity: data.quantity, unitCost: data.unitCost, unit: data.unit || null, supplier: data.supplier || null, notes: data.notes || null, type: data.type || "materials" } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCostsQueryKey(estimateId, lineItemId) });
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          toast({ title: "Cost added" });
          onClose();
        },
        onError: () => toast({ title: "Failed to add cost", variant: "destructive" })
      }
    );
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DialogHeader>
              <DialogTitle>New Internal Cost</DialogTitle>
              <DialogDescription>Track this cost privately. Never shown to the customer.</DialogDescription>
            </DialogHeader>

            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem><FormLabel>Cost Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <div className="grid grid-cols-3 gap-3">
              <FormField control={form.control} name="quantity" render={({ field }) => (
                <FormItem><FormLabel>Qty</FormLabel><FormControl><Input type="number" step="0.01" min="0.01" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="unit" render={({ field }) => (
                <FormItem><FormLabel>Unit</FormLabel><FormControl><Input placeholder="hr, sqft" {...field} /></FormControl></FormItem>
              )} />
              <FormField control={form.control} name="unitCost" render={({ field }) => (
                <FormItem><FormLabel>Cost ($)</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="supplier" render={({ field }) => (
                <FormItem><FormLabel>Supplier <span className="text-muted-foreground font-normal">(optional)</span></FormLabel><FormControl><Input {...field} /></FormControl></FormItem>
              )} />
              <FormField control={form.control} name="notes" render={({ field }) => (
                <FormItem><FormLabel>Notes <span className="text-muted-foreground font-normal">(optional)</span></FormLabel><FormControl><Input {...field} /></FormControl></FormItem>
              )} />
            </div>
            {isPro ? (
              <FormField control={form.control} name="saveToCatalog" render={({ field }) => (
                <FormItem>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={field.value ?? false}
                      onChange={e => field.onChange(e.target.checked)}
                      className="rounded border-input"
                    />
                    <span className="text-sm text-muted-foreground">Save to cost catalog for reuse</span>
                  </label>
                </FormItem>
              )} />
            ) : (
              <p className="text-xs text-muted-foreground">This cost is just for this line. Saving costs to your catalog for reuse is part of Pro.</p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={createCost.isPending || createCatalogItem.isPending}>
                {(createCost.isPending || createCatalogItem.isPending) && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Add Cost
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// CostRow
// ---------------------------------------------------------------------------

function CostRow({ cost, estimateId, lineItemId, onDelete }: any) {
  const isFromProduct = !!cost.fromProductDefaultId;
  const [name, setName] = useState(cost.name || "");
  const [quantity, setQuantity] = useState(String(cost.quantity));
  const [unitCost, setUnitCost] = useState(String(cost.unitCost));
  const costType = cost.type || "materials";
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const updateCost = useUpdateCost();

  useEffect(() => {
    setName(cost.name || "");
    setQuantity(String(cost.quantity));
    setUnitCost(String(cost.unitCost));
  }, [cost.id, cost.name, cost.quantity, cost.unitCost]);

  const localTotal = (parseFloat(quantity) || 0) * (parseFloat(unitCost) || 0);

  const saveField = (opts: { silent?: boolean } = {}) => {
    const qty = parseFloat(quantity);
    const uc = parseFloat(unitCost);
    const invalid = !name.trim() || isNaN(qty) || qty <= 0 || isNaN(uc) || uc < 0;
    if (invalid) {
      // The autosave debounce can fire while a numeric field is transiently
      // empty/invalid mid-edit (e.g. cleared to retype) — stay quiet and let
      // the next debounce tick or the eventual blur validate for real.
      if (opts.silent) return;
      if (!name.trim()) { toast({ title: "Name is required", variant: "destructive" }); return; }
      if (isNaN(qty) || qty <= 0) { toast({ title: "Quantity must be > 0", variant: "destructive" }); return; }
      toast({ title: "Unit cost must be ≥ 0", variant: "destructive" });
      return;
    }
    setSaveStatus("saving");
    updateCost.mutate(
      { id: cost.id, estimateId, lineItemId, data: { name, quantity: qty, unitCost: uc, catalogItemId: cost.catalogItemId ?? null, unit: cost.unit || null, supplier: cost.supplier || null, notes: cost.notes || null, type: costType } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCostsQueryKey(estimateId, lineItemId) });
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 1500);
        },
        onError: () => {
          setSaveStatus("idle");
          toast({ title: "Failed to save cost", variant: "destructive" });
        }
      }
    );
  };

  const isDirty = name !== (cost.name || "") || quantity !== String(cost.quantity) || unitCost !== String(cost.unitCost);
  useAutosaveField(`${name}|${quantity}|${unitCost}`, isDirty, () => saveField({ silent: true }));

  return (
    <div className="p-3 text-sm hover:bg-muted/30 transition-colors group">
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={() => saveField()}
          placeholder="Cost name"
          className="h-7 text-sm font-medium flex-1 min-w-[120px] px-2"
        />
        <span className="inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold shrink-0 bg-muted/60 text-muted-foreground border-border capitalize">
          {costType === "labour" ? "labor" : costType}
        </span>
        {isFromProduct && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium cursor-default shrink-0">
                <Lock className="w-2.5 h-2.5" /> From product
              </span>
            </TooltipTrigger>
            <TooltipContent>Auto-added from product defaults. You can edit or delete it freely.</TooltipContent>
          </Tooltip>
        )}
        {saveStatus === "saving" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground shrink-0" />}
        {saveStatus === "saved" && <span className="text-xs text-green-600 font-medium shrink-0">Saved</span>}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button className="p-1 text-destructive/70 hover:text-destructive shrink-0" onClick={onDelete} aria-label="Delete cost">
              <Trash2 className="w-3 h-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Delete cost</TooltipContent>
        </Tooltip>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <label className="text-xs text-muted-foreground shrink-0">Qty</label>
          <Input
            type="number"
            step="0.01"
            min="0.01"
            value={quantity}
            onChange={e => setQuantity(e.target.value)}
            onBlur={() => saveField()}
            className="w-20 h-6 text-xs px-2"
          />
        </div>
        <span className="text-xs text-muted-foreground">{cost.unit || "ea"} @</span>
        <div className="flex items-center gap-1">
          <label className="text-xs text-muted-foreground shrink-0">$</label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={unitCost}
            onChange={e => setUnitCost(e.target.value)}
            onBlur={() => saveField()}
            className="w-20 h-6 text-xs px-2"
          />
        </div>
        <span className="text-xs font-semibold ml-auto">{formatCurrency(localTotal)}</span>
      </div>
      {cost.supplier && <div className="mt-1 text-xs text-muted-foreground">Via: {cost.supplier}</div>}
      {cost.notes && <div className="mt-0.5 text-xs italic text-muted-foreground/80">{cost.notes}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PerItemWorkshop
// ---------------------------------------------------------------------------

function PerItemWorkshop({ estimateId, lineItem }: any) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();

  const [note, setNote] = useState(lineItem.privateNote || "");
  const [noteDirty, setNoteDirty] = useState(false);
  const [costSearch, setCostSearch] = useState("");
  const [costDropdownOpen, setCostDropdownOpen] = useState(false);
  const costSearchRef = useRef<HTMLInputElement>(null);

  const updateNote = useUpdateLineItemNote();
  const deleteCost = useDeleteCost();
  const createCost = useCreateCost();

  const { data: costs, isLoading: costsLoading } = useListCosts(estimateId, lineItem.id, {
    query: { queryKey: getListCostsQueryKey(estimateId, lineItem.id) }
  });

  const { data: catalog } = useListInternalCatalog({
    query: { queryKey: getListInternalCatalogQueryKey(), enabled: costDropdownOpen }
  });

  const filteredCatalog = catalog?.filter(item =>
    !costSearch || item.name.toLowerCase().includes(costSearch.toLowerCase())
  ) ?? [];

  const saveNote = () => {
    updateNote.mutate(
      { estimateId, lineItemId: lineItem.id, data: { privateNote: note || null } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          setNoteDirty(false);
          toast({ title: "Note saved" });
        }
      }
    );
  };

  const onDeleteCost = (costId: number) => {
    deleteCost.mutate({ estimateId, lineItemId: lineItem.id, id: costId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListCostsQueryKey(estimateId, lineItem.id) });
        queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
      }
    });
  };

  const addCostFromCatalog = (item: { id: number; name: string; unitCost: number; unit?: string | null; type?: string }) => {
    createCost.mutate(
      { estimateId, lineItemId: lineItem.id, data: { catalogItemId: item.id, name: item.name, quantity: 1, unitCost: Number(item.unitCost), unit: item.unit || null, supplier: null, notes: null, type: (item.type || "materials") as "labour" | "materials" | "fees" } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCostsQueryKey(estimateId, lineItem.id) });
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          setCostSearch("");
          setCostDropdownOpen(false);
          emitTourEvent("quote.cost-added");
        },
        onError: () => toast({ title: "Failed to add cost", variant: "destructive" })
      }
    );
  };

  const [createCostModalOpen, setCreateCostModalOpen] = useState(false);
  const [createCostModalName, setCreateCostModalName] = useState("");

  const openCreateCostModal = (name: string) => {
    setCreateCostModalName(name);
    setCostSearch("");
    setCostDropdownOpen(false);
    setCreateCostModalOpen(true);
  };

  const costTotal = costs?.reduce((s, c) => s + c.totalCost, 0) ?? 0;

  return (
    <>
      {createCostModalOpen && (
        <CreateNewCostModal
          estimateId={estimateId}
          lineItemId={lineItem.id}
          initialName={createCostModalName}
          onClose={() => setCreateCostModalOpen(false)}
        />
      )}
    <div className="border-l-4 border-slate-400 bg-slate-50 dark:bg-slate-900/40 px-4 py-4 space-y-5 mx-4 mb-3 rounded-r-md shadow-sm">
      {/* Private Note */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Lock className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Private Note</span>
        </div>
        <Textarea
          value={note}
          onChange={e => { setNote(e.target.value); setNoteDirty(true); }}
          placeholder="Internal reminders, gotchas, schedule notes..."
          className="min-h-[60px] text-sm bg-background"
        />
        {noteDirty && (
          <Button
            size="sm"
            className="mt-2"
            onClick={saveNote}
            disabled={updateNote.isPending}
          >
            {updateNote.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Save Note
          </Button>
        )}
      </div>

      {/* Internal Costs */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <EyeOff className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Internal Costs</span>
          </div>
        </div>

        {costsLoading && <div className="text-xs text-muted-foreground py-2">Loading...</div>}

        {!costsLoading && costs?.length === 0 && !costDropdownOpen && (
          <div className="text-xs text-foreground/70 py-3 text-center border border-dashed border-border rounded-md bg-background/50">
            No internal costs tracked for this item.
          </div>
        )}

        {costs && costs.length > 0 && (
          <div className="border rounded-md divide-y bg-background mb-2">
            {costs.map(cost => (
              <CostRow
                key={cost.id}
                cost={cost}
                estimateId={estimateId}
                lineItemId={lineItem.id}
                onDelete={() => onDeleteCost(cost.id)}
              />
            ))}
            <div className="flex justify-between items-center px-3 py-2 text-xs font-semibold bg-muted/30">
              <span>Total Internal Cost</span>
              <span>{formatCurrency(costTotal)}</span>
            </div>
          </div>
        )}

        {/* Add Cost search input */}
        <div className="relative" data-tour-target="line-item-add-cost">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              ref={costSearchRef}
              placeholder="Add cost — search catalog or type a name..."
              value={costSearch}
              onChange={e => { setCostSearch(e.target.value); setCostDropdownOpen(true); }}
              onFocus={() => setCostDropdownOpen(true)}
              className="pl-8 h-7 text-xs bg-background"
              disabled={createCost.isPending}
            />
            {createCost.isPending && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-muted-foreground" />}
          </div>
          {costDropdownOpen && (
            <div className="absolute z-20 left-0 right-0 top-full mt-1 border rounded-md shadow-lg bg-card overflow-hidden max-h-48 overflow-y-auto">
              {filteredCatalog.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/60 transition-colors border-b last:border-0 text-xs"
                  onMouseDown={e => { e.preventDefault(); addCostFromCatalog(item); }}
                >
                  <span className="font-medium">{item.name}</span>
                  <span className="text-primary font-semibold ml-2">{formatCurrency(Number(item.unitCost))}/{item.unit || "ea"}</span>
                </button>
              ))}
              {costSearch.trim() && !filteredCatalog.some(c => c.name.toLowerCase() === costSearch.trim().toLowerCase()) && (
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/60 transition-colors text-xs text-primary font-medium border-t"
                  onMouseDown={e => { e.preventDefault(); openCreateCostModal(costSearch.trim()); }}
                >
                  <Plus className="w-3 h-3 shrink-0" />
                  Create new cost: "{costSearch.trim()}"
                </button>
              )}
              {!costSearch && filteredCatalog.length === 0 && (
                <p className="text-xs text-muted-foreground px-3 py-2">No catalog items. Type a name to create a custom cost.</p>
              )}
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground border-t bg-muted/20"
                onMouseDown={e => { e.preventDefault(); setCostDropdownOpen(false); setCostSearch(""); }}
              >
                Close
              </button>
            </div>
          )}
        </div>
        {costDropdownOpen && (
          <div className="fixed inset-0 z-10" onClick={() => { setCostDropdownOpen(false); setCostSearch(""); }} />
        )}
      </div>

    </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// LineItemRow
// ---------------------------------------------------------------------------

/** Green "Saved" badge shown in a line item's title bar after an autosave. Also used by invoice line items. */
export function SavedBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800">
      <Check className="w-3 h-3" />
      Saved
    </span>
  );
}

export function LineItemRow({ item, estimateId, allItems, index, groups }: any) {
  const [isExpanded, setIsExpanded] = useState(false);
  // The quote's own target if it has one, else the default (cached query).
  const { data: parentEstimate } = useGetEstimate(estimateId);
  const { tone: marginToneOf } = useTargetMargin(parentEstimate?.targetMargin);
  const [description, setDescription] = useState(item.description || "");
  const [quantity, setQuantity] = useState(String(item.quantity));
  const [unitPrice, setUnitPrice] = useState(String(item.unitPrice));
  const [localProductId, setLocalProductId] = useState<number | null>(item.productId ?? null);
  const [localProductName, setLocalProductName] = useState<string | null>(item.productName ?? null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [quickProductOpen, setQuickProductOpen] = useState(false);

  useEffect(() => {
    setDescription(item.description || "");
    setQuantity(String(item.quantity));
    setUnitPrice(String(item.unitPrice));
    setLocalProductId(item.productId ?? null);
    setLocalProductName(item.productName ?? null);
  }, [item.id, item.description, item.quantity, item.unitPrice, item.productId, item.productName]);

  const deleteLineItem = useDeleteLineItem();
  const updateLineItem = useUpdateLineItem();
  const reorderLineItems = useReorderEstimateLineItems();
  const createLineItemDup = useCreateLineItem();
  const createCostDup = useCreateCost();
  const updateNoteDup = useUpdateLineItemNote();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const [isDuplicating, setIsDuplicating] = useState(false);

  const { data: products } = useListProducts(
    productSearch ? { search: productSearch } : {},
    { query: { queryKey: getListProductsQueryKey(productSearch ? { search: productSearch } : {}), enabled: productSearchOpen } }
  );

  const saveField = (overrides?: { description?: string; quantity?: string; unitPrice?: string; silent?: boolean }) => {
    const desc = overrides?.description ?? description;
    const qty = parseFloat(overrides?.quantity ?? quantity);
    const price = parseFloat(overrides?.unitPrice ?? unitPrice);
    const invalid = !desc.trim() || isNaN(qty) || qty <= 0 || qty > 99999999 || isNaN(price) || Math.abs(price) > 99999999.99;
    if (invalid) {
      // See CostRow's saveField — the autosave debounce can catch a field
      // mid-edit (e.g. momentarily empty while retyping); stay quiet then.
      if (overrides?.silent) return;
      if (!desc.trim()) { toast({ title: "Description is required", variant: "destructive" }); return; }
      if (isNaN(qty) || qty <= 0) { toast({ title: "Quantity must be greater than 0", variant: "destructive" }); return; }
      if (qty > 99999999) { toast({ title: "Maximum value is 99,999,999", variant: "destructive" }); return; }
      if (isNaN(price)) { toast({ title: "Unit price is required", variant: "destructive" }); return; }
      toast({ title: "Maximum value is 99,999,999", variant: "destructive" });
      return;
    }
    setSaveStatus("saving");
    updateLineItem.mutate(
      { id: item.id, estimateId, data: { description: desc, quantity: qty, unitPrice: price, productId: localProductId, sortOrder: item.sortOrder } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 1500);
        },
        onError: () => {
          setSaveStatus("idle");
          toast({ title: "Failed to save", variant: "destructive" });
        }
      }
    );
  };

  const isFieldDirty = description !== (item.description || "") || quantity !== String(item.quantity) || unitPrice !== String(item.unitPrice);
  useAutosaveField(`${description}|${quantity}|${unitPrice}`, isFieldDirty, () => saveField({ silent: true }));

  const applyProduct = (product: { id: number; name: string; description?: string | null; unitPrice: number }) => {
    const newDesc = description || product.description || product.name;
    const newPrice = String(product.unitPrice);
    setDescription(newDesc);
    setUnitPrice(newPrice);
    setLocalProductId(product.id);
    setLocalProductName(product.name);
    setProductSearchOpen(false);
    setProductSearch("");
    setSaveStatus("saving");
    updateLineItem.mutate(
      { id: item.id, estimateId, data: { description: newDesc, quantity: parseFloat(quantity) || 1, unitPrice: product.unitPrice, productId: product.id, sortOrder: item.sortOrder } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 1500);
        },
        onError: () => {
          setSaveStatus("idle");
          toast({ title: "Failed to save", variant: "destructive" });
        }
      }
    );
  };

  const onDelete = () => {
    deleteLineItem.mutate({ id: item.id, estimateId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
        toast({ title: "Item removed" });
      }
    });
  };

  const handleDuplicate = async () => {
    setIsDuplicating(true);
    try {
      const newItem = await new Promise<{ id: number }>((resolve, reject) => {
        createLineItemDup.mutate(
          { estimateId, data: { description: item.description, quantity: item.quantity, unitPrice: item.unitPrice, productId: item.productId ?? null, groupId: item.groupId ?? null } },
          { onSuccess: resolve, onError: reject }
        );
      });
      const materials = item.materials ?? [];
      for (const mat of materials) {
        await new Promise<void>((resolve, reject) => {
          createCostDup.mutate(
            { estimateId, lineItemId: newItem.id, data: { name: mat.name, quantity: mat.quantity, unitCost: mat.unitCost, unit: mat.unit ?? null, supplier: mat.supplier ?? null, notes: mat.notes ?? null, type: (mat.type || "materials") as "labour" | "materials" | "fees", catalogItemId: mat.catalogItemId ?? null } },
            { onSuccess: () => resolve(), onError: reject }
          );
        });
      }
      if (item.privateNote) {
        await new Promise<void>((resolve, reject) => {
          updateNoteDup.mutate(
            { estimateId, lineItemId: newItem.id, data: { privateNote: item.privateNote } },
            { onSuccess: () => resolve(), onError: reject }
          );
        });
      }
      queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
      toast({ title: "Item duplicated" });
    } catch {
      toast({ title: "Failed to duplicate item", variant: "destructive" });
    } finally {
      setIsDuplicating(false);
    }
  };

  const [isGroupMoving, setIsGroupMoving] = useState(false);
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);

  const moveToGroup = (targetGroupId: number | null) => {
    setGroupMenuOpen(false);
    if (targetGroupId === (item.groupId ?? null)) return;
    setIsGroupMoving(true);
    updateLineItem.mutate(
      { id: item.id, estimateId, data: { description, quantity: parseFloat(quantity) || 1, unitPrice: parseFloat(unitPrice) || 0, productId: localProductId, sortOrder: item.sortOrder, groupId: targetGroupId } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          setIsGroupMoving(false);
        },
        onError: () => {
          setIsGroupMoving(false);
          toast({ title: "Failed to move item", variant: "destructive" });
        }
      }
    );
  };

  const [isReordering, setIsReordering] = useState(false);

  const reorderItem = (direction: "up" | "down") => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= allItems.length || isReordering) return;
    setIsReordering(true);
    const newOrder = allItems.map((i: any) => i.id);
    newOrder.splice(index, 1);
    newOrder.splice(swapIndex, 0, item.id);
    reorderLineItems.mutate(
      { estimateId, ids: newOrder },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          setIsReordering(false);
        },
        onError: () => {
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          setIsReordering(false);
          toast({ title: "Failed to reorder item", variant: "destructive" });
        }
      }
    );
  };

  const localTotal = (parseFloat(quantity) || 0) * (parseFloat(unitPrice) || 0);

  return (
    <>
    <Dialog open={quickProductOpen} onOpenChange={setQuickProductOpen}>
      <QuickCreateProductDialog
        initialName={productSearch || description}
        onCreated={(p) => { applyProduct(p); setQuickProductOpen(false); }}
        onClose={() => setQuickProductOpen(false)}
      />
    </Dialog>
    <div>
      <div className="px-4 py-3 flex gap-3 hover:bg-muted/20 transition-colors group">
        <div className="mt-2 flex flex-col gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => reorderItem("up")}
            disabled={index === 0 || isReordering}
            className="p-1 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            aria-label="Move item up"
          >
            <ChevronUp className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => reorderItem("down")}
            disabled={index === allItems.length - 1 || isReordering}
            className="p-1 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            aria-label="Move item down"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          {/* Product chip / change link */}
          <div className="flex items-center gap-2 flex-wrap">
            {localProductName ? (
              <span className="flex items-center justify-between w-full text-base px-3 py-2 rounded-md bg-[hsl(var(--sidebar))] text-[hsl(var(--primary))] font-semibold">
                {localProductName}
                {/* Save indicator sits inside the chip so it never wraps onto its own line and pushes the description down */}
                <span className="flex items-center gap-2 ml-2 shrink-0">
                  {saveStatus === "saving" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                  {saveStatus === "saved" && <SavedBadge />}
                  <button
                    type="button"
                    className="text-[hsl(var(--primary))] hover:opacity-70 transition-opacity"
                    onClick={() => { setProductSearchOpen(v => !v); setProductSearch(""); }}
                    title="Change product"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </span>
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className="text-base text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                  onClick={() => { setProductSearchOpen(v => !v); setProductSearch(""); }}
                >
                  <Search className="w-5 h-5" />
                  Link product
                </button>
                {saveStatus === "saving" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                {saveStatus === "saved" && <SavedBadge />}
              </>
            )}
          </div>

          {/* Inline product search popover */}
          {productSearchOpen && (
            <div className="border rounded-md shadow-md bg-card p-2 space-y-1 z-10 relative">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  autoFocus
                  placeholder="Search catalog..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="pl-8 text-sm h-8"
                />
              </div>
              {productSearch && products !== undefined && (
                <div className="max-h-40 overflow-y-auto border rounded bg-background">
                  {products.map(p => (
                    <button key={p.id} type="button"
                      className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/60 transition-colors border-b last:border-0"
                      onClick={() => applyProduct(p)}>
                      <div>
                        <p className="text-sm font-medium">{p.name}</p>
                        {p.description && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{p.description}</p>}
                      </div>
                      <span className="text-sm font-semibold text-primary shrink-0 ml-2">{formatCurrency(Number(p.unitPrice))}</span>
                    </button>
                  ))}
                  {products.length === 0 && (
                    <p className="text-xs text-muted-foreground px-3 py-2">No products found</p>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between pt-0.5">
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => { setProductSearchOpen(false); setProductSearch(""); }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                  onClick={() => { setProductSearchOpen(false); setQuickProductOpen(true); }}
                >
                  <Plus className="w-3 h-3" />
                  New product
                </button>
              </div>
            </div>
          )}

          {/* Two-column layout: description left, qty/price right */}
          <div className="flex gap-3 items-stretch">
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              onBlur={() => saveField({ description })}
              placeholder="Describe this line item..."
              className="flex-1 min-h-[72px] text-sm resize-y"
            />

            <div className="flex flex-col items-end justify-between gap-2 shrink-0">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-1">
                  <label className="text-sm text-muted-foreground shrink-0 w-8 text-right">Qty</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={quantity}
                    onChange={e => setQuantity(e.target.value)}
                    onBlur={() => saveField({ quantity })}
                    className="w-24 h-7 text-sm px-2"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <label className="text-sm text-muted-foreground shrink-0 w-8 text-right">$</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={unitPrice}
                    onChange={e => setUnitPrice(e.target.value)}
                    onBlur={() => saveField({ unitPrice })}
                    className="w-24 h-7 text-sm px-2"
                  />
                </div>
              </div>

              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-1.5">
                  {groups && groups.length > 0 && (
                    <DropdownMenu open={groupMenuOpen} onOpenChange={setGroupMenuOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-9 w-9 text-muted-foreground hover:text-foreground"
                          disabled={isGroupMoving}
                          title="Move to group"
                        >
                          {isGroupMoving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Group className="w-4 h-4" />}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {item.groupId && (
                          <>
                            <DropdownMenuItem onClick={() => moveToGroup(null)}>
                              Remove from group
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        {(groups as LineItemGroup[]).filter((g) => g.id !== item.groupId).map((g) => (
                          <DropdownMenuItem key={g.id} onClick={() => moveToGroup(g.id)}>
                            Move to: {g.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-muted-foreground hover:text-foreground" onClick={handleDuplicate} disabled={isDuplicating} aria-label="Duplicate item">
                        {isDuplicating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Duplicate item</TooltipContent>
                  </Tooltip>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={onDelete} aria-label="Delete item">
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Delete item</TooltipContent>
                  </Tooltip>
                </div>
                <span className="font-semibold text-sm">{formatCurrency(localTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Costs & Notes expandable subtitle */}
      <button
        type="button"
        onClick={() => {
          const expanding = !isExpanded;
          setIsExpanded(v => !v);
          if (expanding) emitTourEvent("quote.costs-expanded");
        }}
        className="w-full flex items-center flex-wrap gap-1.5 px-4 py-1.5 text-xs font-medium text-muted-foreground hover:text-primary hover:bg-muted/30 transition-colors border-t border-dashed border-border/60"
        data-tour-target="quote-costs-toggle"
      >
        <span className="flex items-center gap-1.5 shrink-0">
          {isExpanded ? <ChevronUp className="w-3.5 h-3.5 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0" />}
          Costs &amp; Notes
        </span>
        {item.internalCostTotal > 0 && (() => {
          const mats = item.materials ?? [];
          const totals: Record<ParentCategory, number> = { Labor: 0, Materials: 0, Fees: 0 };
          for (const m of mats) {
            totals[costTypeToParent(m.type)] += m.totalCost;
          }
          const catStyles: Record<ParentCategory, string> = {
            Labor: "bg-blue-50 text-blue-700 border-blue-200",
            Materials: "bg-amber-50 text-amber-700 border-amber-200",
            Fees: "bg-slate-50 text-slate-600 border-slate-200",
          };
          return (
            <span className="ml-auto flex items-center gap-1.5 flex-wrap justify-end">
              {PARENT_CATEGORIES.filter(cat => totals[cat] > 0).map(cat => (
                <span key={cat} className={`px-2 py-0.5 rounded border text-xs font-semibold tabular-nums whitespace-nowrap ${catStyles[cat]}`}>
                  {cat}: {formatCurrency(totals[cat])}
                </span>
              ))}
              <span className="px-2 py-0.5 bg-muted text-foreground rounded border border-border text-xs font-semibold tabular-nums whitespace-nowrap">
                Cost: {formatCurrency(item.internalCostTotal)}
              </span>
              {(() => {
                const lineTotal = item.quantity * item.unitPrice;
                if (lineTotal <= 0) return null;
                const profit = lineTotal - item.internalCostTotal;
                const margin = (profit / lineTotal) * 100;
                // Green at or above the target margin (Settings), amber below it,
                // red below half of it.
                const colorClass = {
                  good: "bg-green-50 text-green-700 border-green-200",
                  thin: "bg-amber-50 text-amber-700 border-amber-200",
                  low: "bg-red-50 text-red-700 border-red-200",
                }[marginToneOf(margin)];
                return (
                  <span className={`px-2 py-0.5 rounded border text-xs font-semibold tabular-nums whitespace-nowrap ${colorClass}`}>
                    Profit: {formatCurrency(profit)} · {margin.toFixed(0)}%
                  </span>
                );
              })()}
            </span>
          );
        })()}
        {item.privateNote && (
          <span className={`px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded border border-purple-200 ${item.internalCostTotal > 0 ? "" : "ml-auto"}`}>
            Note
          </span>
        )}
      </button>

      {isExpanded && (
        <PerItemWorkshop estimateId={estimateId} lineItem={item} />
      )}
    </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// EstimateLineItemSection  — the main exported wrapper
// ---------------------------------------------------------------------------

/**
 * Renders the full line-item editor for a given estimate.
 * Used in both the quote builder (estimates/detail.tsx) and the Change Orders
 * tab (projects/detail.tsx).
 *
 * @param estimateId   — the estimate to render line items for
 * @param showSubtotal — when true shows a simple subtotal bar at the bottom
 *                       (set to false if the parent already renders PricingSummary)
 */
export function EstimateLineItemSection({
  estimateId,
  showSubtotal = true,
}: {
  estimateId: number;
  showSubtotal?: boolean;
}) {
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();

  const { data: estimate, isLoading } = useGetEstimate(estimateId);
  const [addLineItemOpen, setAddLineItemOpen] = useState(false);
  const [addingToGroupId, setAddingToGroupId] = useState<number | null>(null);
  const createGroup = useCreateLineItemGroup();

  const handleAddGroup = () => {
    if (!estimateId) return;
    const groups = estimate?.groups ?? [];
    const maxSort = groups.reduce((m, g) => Math.max(m, g.sortOrder ?? 0), -1);
    const accentColor = GROUP_ACCENT_COLORS[groups.length % GROUP_ACCENT_COLORS.length].hex;
    createGroup.mutate(
      { estimateId, data: { name: "New Group", sortOrder: maxSort + 1, accentColor } },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) }),
        onError: () => toast({ title: "Failed to add group", variant: "destructive" }),
      }
    );
  };

  if (isLoading || !estimate) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        Loading…
      </div>
    );
  }

  const sortedGroups = [...(estimate.groups ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const sortedItems = [...estimate.lineItems].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  const ungroupedItems = sortedItems.filter(li => !li.groupId);
  const subtotal = sortedItems.reduce((s, li) => s + Number(li.quantity) * Number(li.unitPrice), 0);

  return (
    <Card className="shadow-sm border-border/50">
      <CardHeader className="flex flex-row items-center justify-between pb-2 border-b gap-3 bg-muted/40">
        <div className="shrink-0">
          <CardTitle className="text-lg">Change Order Items</CardTitle>
          <CardDescription>Line items pending attachment to a quote or invoice</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1">
                <Plus className="w-3.5 h-3.5" />
                Add
                <ChevronDown className="w-3 h-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => setAddLineItemOpen(true)}>
                <Plus className="w-3.5 h-3.5 mr-2" />
                Add Line Item
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleAddGroup} disabled={createGroup.isPending}>
                {createGroup.isPending ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-2" />}
                Add Group
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      {addLineItemOpen && (
        <AddItemSearch estimateId={estimateId} onClose={() => setAddLineItemOpen(false)} />
      )}
      {addingToGroupId !== null && (
        <AddItemSearch estimateId={estimateId} groupId={addingToGroupId} onClose={() => setAddingToGroupId(null)} />
      )}
      <CardContent className="p-0">
        <div className="border-b">
          <div className="divide-y divide-gray-300">
            {ungroupedItems.map((item) => (
              <LineItemRow
                key={item.id}
                item={item}
                estimateId={estimateId}
                allItems={sortedItems}
                index={sortedItems.findIndex(i => i.id === item.id)}
                groups={sortedGroups}
              />
            ))}
            <button
              type="button"
              onClick={() => setAddLineItemOpen(true)}
              className="w-full flex items-center justify-center gap-2 py-3.5 text-sm text-muted-foreground hover:text-primary hover:bg-muted/30 border-t border-dashed border-border/50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add line item
            </button>
          </div>
          {sortedGroups.map((group) => {
            const groupItems = sortedItems.filter(li => li.groupId === group.id);
            return (
              <GroupHeader
                key={group.id}
                group={group}
                estimateId={estimateId}
                items={groupItems}
                allGroups={sortedGroups}
              >
                <div className="divide-y divide-gray-300">
                  {groupItems.map((item) => (
                    <LineItemRow
                      key={item.id}
                      item={item}
                      estimateId={estimateId}
                      allItems={sortedItems}
                      index={sortedItems.findIndex(i => i.id === item.id)}
                      groups={sortedGroups}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setAddingToGroupId(group.id)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-sm text-muted-foreground hover:text-primary hover:bg-muted/30 border-dashed border-t border-border/50 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add line item
                  </button>
                </div>
              </GroupHeader>
            );
          })}
        </div>
        {showSubtotal && (
          <div className="flex items-center justify-between px-4 py-3 bg-muted/10 border-t">
            <span className="text-sm font-medium text-muted-foreground">Subtotal</span>
            <span className="text-base font-semibold">{formatCurrency(subtotal)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
